import { useState, useEffect } from "react";
import axios from "axios";
import { ANIMALI } from "@/data/animali";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, RotateCcw, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Ruota() {
  const { getToken } = useAuth();
  const [animaleIndex, setAnimaleIndex] = useState(null); // null = no draw yet today
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);

  const animale = animaleIndex != null ? ANIMALI[animaleIndex] : null;

  // On mount: check if user already has a totem for today
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API}/totem/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && data && typeof data.animale_index === "number") {
          setAnimaleIndex(data.animale_index);
          setRevealed(true);
        }
      } catch {
        // No draw yet or transient error — wheel stays closed
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDraw = async () => {
    if (drawing || animaleIndex != null) return;
    setDrawing(true);
    // Random extraction — first tap of the day
    const idx = Math.floor(Math.random() * ANIMALI.length);
    try {
      const token = getToken();
      const { data } = await axios.post(
        `${API}/totem/today`,
        { animale_index: idx },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend is idempotent — if another draw was already saved, use that
      const finalIdx = typeof data?.animale_index === "number" ? data.animale_index : idx;
      setAnimaleIndex(finalIdx);
      setRevealed(true);
    } catch {
      // Network failure: still reveal locally (best-effort)
      setAnimaleIndex(idx);
      setRevealed(true);
    } finally {
      setDrawing(false);
    }
  };

  return (
    <div data-testid="ruota-screen">
      <section className="lsw-hero-gradient px-6 pt-10 pb-6 rounded-b-[32px]">
        <p className="font-body font-light text-[11px] tracking-[0.4em] uppercase text-[#941a7c]">Divinazione</p>
        <h1 className="font-serif-display font-light text-[34px] leading-[1.05] tracking-tight text-[#2e2820] mt-2">
          Ruota degli <span className="italic text-[#e07818] font-normal">Animali Totem</span>
        </h1>
        <p className="font-body font-light text-base text-[#7a6f65] leading-relaxed mt-3">
          Tocca la ruota per estrarre il tuo animale guida di oggi. La tua estrazione resta tua per tutta la giornata.
        </p>
      </section>

      <div className="px-6 pt-8 pb-8 flex flex-col items-center">
        {loading ? (
          <div className="w-[280px] h-[280px] flex items-center justify-center text-[#7a6f65]">
            <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.5} />
          </div>
        ) : !revealed ? (
          <button
            data-testid="ruota-tap"
            onClick={handleDraw}
            disabled={drawing}
            className="relative w-[280px] h-[280px] rounded-full flex items-center justify-center group active:scale-95 transition-transform disabled:opacity-70"
            aria-label="Estrai animale del giorno"
          >
            <span className="absolute inset-0 rounded-full" style={{ background: "conic-gradient(from 0deg, #e07818, #2e9020, #1535a0, #941a7c, #e07818)", animation: drawing ? "lsw-spin 1.2s linear infinite" : "lsw-spin 18s linear infinite", filter: "blur(0.5px)", opacity: 0.85 }} />
            <span className="absolute inset-3 rounded-full bg-[#f9f5ef]" />
            <span className="absolute inset-6 rounded-full border-2 border-dashed border-[#e07818]/40" style={{ animation: drawing ? "lsw-spin-rev 1.8s linear infinite" : "lsw-spin-rev 30s linear infinite" }} />
            <span className="absolute inset-12 rounded-full border border-[#941a7c]/30" />
            <span className="absolute inset-16 rounded-full bg-gradient-to-br from-[#e07818]/15 via-[#941a7c]/10 to-[#1535a0]/15 flex flex-col items-center justify-center text-center px-4">
              <Sparkles strokeWidth={1.5} className="w-7 h-7 text-[#e07818] mb-2" />
              <span className="font-serif-display italic text-[#2e2820] text-xl leading-tight">
                {drawing ? "Estraendo…" : "Tocca per estrarre"}
              </span>
              <span className="font-body text-[10px] tracking-[0.3em] uppercase text-[#7a6f65] mt-2">
                il tuo animale di oggi
              </span>
            </span>
          </button>
        ) : (
          <article
            data-testid="ruota-card"
            className="w-full bg-[#f9f5ef] rounded-[22px] overflow-hidden border border-[#ede7df] shadow-[0_20px_40px_-20px_rgba(46,40,32,0.35)]"
            style={{ borderTop: "3px solid #e07818", animation: "lsw-flip 0.7s ease-out both" }}
          >
            {animale?.image ? (
              <img src={animale.image} alt={animale.nome} className="w-full aspect-square object-cover" data-testid="ruota-card-image" />
            ) : (
              <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-[#fef2e0] via-[#f8e4f4] to-[#e4e8f8]">
                <span className="font-serif-display italic text-[#7a6f65] text-2xl">{animale?.nome}</span>
              </div>
            )}
            <div className="p-6">
              <p data-testid="ruota-keyword" className="font-body font-medium text-[11px] tracking-[0.4em] uppercase text-[#e07818]">
                {animale?.keyword}
              </p>
              <h2 data-testid="ruota-nome" className="font-serif-display font-light text-[40px] leading-none tracking-tight text-[#2e2820] mt-2">
                {animale?.nome}
              </h2>
              <div className="mt-4 flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-gradient-to-r from-[#e07818]/0 via-[#e07818]/70 to-[#e07818]/0" />
              </div>
              <p data-testid="ruota-desc" className="font-serif-display italic font-light text-[19px] leading-[1.4] text-[#2e2820] mt-4">
                “{animale?.desc}”
              </p>
              <p data-testid="ruota-message" className="font-body font-light text-sm text-[#7a6f65] leading-relaxed mt-4">
                {animale?.message}
              </p>
              <p className="font-body font-light text-[11px] tracking-[0.2em] uppercase text-[#7a6f65] mt-6 pt-4 border-t border-[#ede7df]">
                La tua estrazione di oggi · Torna domani per un nuovo animale
              </p>
            </div>
          </article>
        )}
      </div>

      <style>{`
        @keyframes lsw-spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }
        @keyframes lsw-spin-rev { from{transform:rotate(360deg)} to{transform:rotate(0)} }
        @keyframes lsw-flip { from { opacity:0; transform: rotateY(40deg) scale(0.92); } to { opacity:1; transform: rotateY(0) scale(1); } }
      `}</style>
    </div>
  );
}
